'use client';

import { useTranslation } from 'next-i18next';
import { useSession } from 'next-auth/react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { Button } from '@components/common';
import { useBestieChatStateOptional } from '@components/contexts/BestieChatStateContext';

/**
 * Loads the thread's past messages into the chat, on demand.
 *
 * **Guests only.** Signed-in users are on rolling 3h sessions: a fresh session has nothing to load,
 * and a resumed one restores itself automatically, so the button would either no-op or duplicate work
 * the provider already did. The consequence, which is intended: past sessions are not reachable from
 * the UI. Guests still have one permanent thread and no auto-restore, so the button is their only way
 * back to earlier messages.
 *
 * It is a one-shot control — once history is merged in there is nothing left to do, so the button
 * unmounts. A failed load leaves it in place so the click can be retried.
 *
 * Renders nothing outside `BestieChatStateProvider`, which keeps it inert for the other
 * `ChatBranch` callers (stories answer bar, interview answer bar, job-list search bar).
 */
export const ChatHistoryButton = () => {
  const { t } = useTranslation('common');
  const { status } = useSession();
  const chatState = useBestieChatStateOptional();

  if (status === 'authenticated') return null;
  if (!chatState || chatState.hasHistoryLoaded) return null;

  const { loadHistory, isHistoryLoading } = chatState;

  return (
    <Button
      type="button"
      styling="outline"
      color="light"
      rounding="none"
      isLoading={isHistoryLoading}
      aria-label={t('global.label.history', 'History')}
      className="h-9! w-9! shrink-0 rounded-lg! border-none! bg-surface-100 p-0! transition-transform hover:scale-110 lg:h-10! lg:w-10!"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => { void loadHistory(); }}
    >
      {isHistoryLoading ? null : <ClockIcon className="size-5 text-ink" />}
    </Button>
  );
};
